import React, { useRef } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Eye, Compass, Target, CheckCircle2 } from 'lucide-react';

export default function About() {
  const coreValues = [
    { name: 'Integrity', desc: 'Operating with absolute honesty and compliance in all pharmaceutical shipments.' },
    { name: 'Trust', desc: 'Cultivating decades of absolute transparency with Sangli area doctors and hospitals.' },
    { name: 'Quality', desc: 'Supplying only authentic, FDA-approved critical supplies and certified OT installations.' },
    { name: 'Innovation', desc: 'Bringing modern Modular Operation Theatre and medical gas setups to Sangli district.' },
    { name: 'Commitment', desc: 'Providing round-the-clock logistical support for life-saving hospital materials.' },
    { name: 'Customer Success', desc: 'Partnering in your growth by offering compliant, space-efficient hospital layout advice.' }
  ];

  const milestones = [
    { year: 'Around 1980', title: 'Kiran Chemist Established', desc: 'Started by late Kiran Harakchand Shah as a rented 200 sq. ft. retail medical shop in Gandhi Chowk, Miraj.' },
    { year: '1996', title: 'Generational Transition', desc: 'Following the sad demise of the founder, the business was continued with no prior experience, expanding retail operations fourfold within two years.' },
    { year: '1998', title: 'Kiran Distributors Founded', desc: 'Launched wholesale operations with just two pharmaceutical companies, focusing on building a reliable distribution network.' },
    { year: '2003', title: 'Regional Footprint Expansion', desc: 'Grew distribution portfolio to 15 leading companies, expanding our delivery reach to cover the entire Sangli district and parts of Karnataka.' },
    { year: '2020', title: 'State-of-the-Art Infrastructure', desc: 'Demolished the old premises to construct a modern 3,500 sq. ft. facility, housing wholesale distribution operations, retail counters, and path labs.' },
    { year: 'Today', title: 'Leading Hospital Setup Vendor', desc: 'Serving as a distributor for 40+ leading brands and a single-source vendor for major hospitals, providing custom modular OT layouts and medical equipment setups.' }
  ];

  const timelineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start 70%", "end 50%"]
  });
  const scaleY = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <div className="font-sans bg-brand-light">

      {/* Page Header */}
      <section className="bg-brand-navy text-white pt-28 pb-16 relative overflow-hidden grid-mesh">
        <div className="absolute inset-0 bg-brand-navy/80 z-10" />
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/WhatsApp Image 2026-05-31 at 11.46.24 AM.jpeg"
            alt="Hospital Facility Setup"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20 text-center space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-green font-poppins block">Get To Know Us</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-montserrat text-white tracking-tight">
            About Kiran Distributors
          </h1>
          <p className="text-sm text-brand-silver max-w-2xl mx-auto font-poppins">
            A premier healthcare solutions provider, ensuring clinical reliability, logistical compliance, and state-of-the-art operation setups.
          </p>
        </div>
      </section>

      {/* Company Overview & History */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-mesh opacity-5 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">

            <div className="lg:col-span-6 space-y-6">
              <span className="px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-brand-blue bg-brand-blue/10 rounded-full font-poppins inline-block">
                Our History
              </span>

              <h2 className="text-3xl sm:text-4xl font-extrabold font-montserrat text-brand-navy tracking-tight leading-tight">
                Decades of Dedicated <span className="text-brand-blue">Healthcare Support</span>
              </h2>

              <div className="space-y-4 text-sm text-gray-600 font-poppins leading-relaxed">
                <p className="text-base font-semibold text-gray-550 leading-relaxed font-poppins">
                  Initially, there was a rented retail medical shop named <strong className="font-bold text-gray-900">Kiran Chemist</strong>, started by my late father, <strong className="font-bold text-gray-900">Kiran Harakchand Shah</strong>, around 1980. He passed away in 1996, and without any experience, <strong className="font-bold text-gray-900">Me</strong> and my brother <strong className="font-bold text-gray-900">Samir Kiran Shah</strong> had to continue the business.
                </p>
                <p>
                  However, within 2 years, We expanded the business fourfold together. Then to support this growth I started <strong className="font-bold text-gray-900">Kiran Distributors</strong> as a wholesale business with just 2 companies. Within 5 years, the number grew to 15 companies, covering the entire Sangli district and parts of Karnataka.
                </p>
                <p>
                  Today, we are distributors for more than 40 leading companies and are major vendors for institutions and leading hospitals across the Sangli district.
                </p>
                <p>
                  Recently, we have expanded into hospital furniture, medical equipment, and Modular OT setups, offering one-stop solutions for both upcoming and existing hospitals.
                </p>
              </div>
            </div>

            <div className="lg:col-span-6 relative group h-[450px] w-full">
              {/* Soft decorative shadow border glow */}
              <div className="absolute -inset-2 rounded-3xl bg-gradient-to-tr from-brand-blue/10 to-brand-green/10 blur-lg opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200" />

              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 h-full w-full bg-brand-navy">
                <img
                  src="/assets/WhatsApp Image 2026-05-31 at 11.50.15 AM.jpeg"
                  alt="Hospital Supply Shipping Warehouse"
                  className="w-full h-full object-cover object-right transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 via-transparent to-transparent" />

                {/* Floating Card 1: Est. 1980 */}
                <div className="absolute top-6 left-6 glass-panel-dark px-4 py-2.5 rounded-xl shadow-lg border border-white/10 flex items-center gap-2.5 animate-float">
                  <div className="w-2.5 h-2.5 rounded-full bg-brand-blue animate-pulse" />
                  <div className="text-left">
                    <p className="text-[10px] text-white/60 uppercase font-semibold font-poppins tracking-wider">Established</p>
                    <p className="text-xs font-bold text-white font-montserrat">1980</p>
                  </div>
                </div>

                {/* Floating Card 2: 40+ Brands */}
                <div className="absolute bottom-6 right-6 glass-panel-dark px-4 py-2.5 rounded-xl shadow-lg border border-white/10 flex items-center gap-2.5 animate-float-delayed">
                  <div className="w-7 h-7 rounded-lg bg-brand-blue/20 text-brand-blue flex items-center justify-center font-bold text-xs font-montserrat border border-brand-blue/30">
                    40+
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] text-white/60 uppercase font-semibold font-poppins tracking-wider">Brand Alliances</p>
                    <p className="text-xs font-bold text-white font-montserrat">Leading Manufacturers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Information */}
      <section className="py-24 bg-brand-light relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/30 grid grid-cols-1 lg:grid-cols-12 gap-0 relative">

            {/* Background Quotes Watermark */}
            <div className="absolute top-6 right-8 text-brand-blue/[0.04] font-serif text-[180px] leading-none pointer-events-none select-none hidden lg:block">
              “
            </div>

            {/* Left: Founder Picture Frame */}
            <div className="lg:col-span-5 h-[400px] lg:h-auto bg-brand-navy relative overflow-hidden flex items-center justify-center group">
              <img
                src="/assets/Founder - Kiran Distributor.png"
                alt="Mr. Kalpesh Shah - Founder of Kiran Distributors"
                className="w-full h-full object-cover object-[35%_center] transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-brand-navy/20 to-transparent" />
              <div className="absolute bottom-8 left-8 text-white z-10 space-y-1">
                <h4 className="text-2xl font-bold font-montserrat text-white tracking-tight">Mr. Kalpesh Shah</h4>
                <p className="text-xs text-brand-blue font-poppins font-semibold uppercase tracking-wider">Founder & Managing Director</p>
              </div>
            </div>

            {/* Right: Founder Profile Details */}
            <div className="lg:col-span-7 p-8 lg:p-14 space-y-8 justify-center flex flex-col relative z-10">
              <div className="space-y-3">
                <span className="px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-brand-blue bg-brand-blue/10 rounded-full font-poppins inline-block">
                  Executive Profile
                </span>
                <h3 className="text-3xl font-extrabold font-montserrat text-brand-navy tracking-tight leading-tight">
                  Visionary Leadership in Medicine
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="p-4 rounded-2xl bg-brand-light border border-brand-silver/80 flex items-center justify-between">
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-gray-400 tracking-wider font-poppins mb-0.5">Industry Standing</span>
                    <p className="text-sm font-bold text-brand-navy font-montserrat">More than 25 Years of Relentless Growth</p>
                  </div>
                </div>
              </div>

              <div className="space-y-6 text-sm text-gray-600 font-poppins leading-relaxed">
                <p>
                  "I completed my schooling up to 7th standard at St. Stanislaus School, Bandra, Mumbai. I then studied at New English School, Miraj, until 10th standard. Further, I completed my B.Sc. graduation from Willingdon College, Sangli, and later earned a D.Pharm degree from Wanless Pharmacy College, Miraj."
                </p>

                {/* Highlighted Quote Block */}
                <div className="relative border-l-4 border-brand-blue pl-4 py-3 bg-brand-light/75 rounded-r-2xl my-6">
                  <p className="text-sm font-semibold text-gray-800 leading-relaxed font-poppins">
                    "On the day of my pharmacy result, my father expired, so I had to immediately join <strong className="font-bold text-gray-900">Kiran Chemist</strong> without any experience. Facing financial and operational challenges for 2 years, I pushed forward—and after that, I never looked back."
                  </p>
                </div>

                <p>
                  "We had <strong className="font-bold text-gray-900">Kiran Chemist</strong>, a rented 200 sq. ft. shop, for 15 years, but within 5 years of my joining, we acquired the entire building, including the shop, which totaled 1,000 sq. ft."
                </p>

                {/* Building Specs Modern Cards */}
                <div className="space-y-3 pt-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest font-poppins">Facility Infrastructure (3,500 sq. ft.)</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-3.5 rounded-xl border border-brand-silver/80 bg-brand-light hover:border-brand-blue/30 transition-all duration-300">
                      <span className="text-[10px] font-bold text-brand-blue font-poppins block uppercase mb-1">Ground Floor</span>
                      <p className="text-xs font-bold text-brand-navy font-montserrat leading-tight">Wellness Forever</p>
                      <p className="text-[10px] text-gray-500 font-poppins mt-0.5">Retail Pharmacy Store</p>
                    </div>
                    <div className="p-3.5 rounded-xl border border-brand-silver/80 bg-brand-light hover:border-brand-blue/30 transition-all duration-300">
                      <span className="text-[10px] font-bold text-brand-blue font-poppins block uppercase mb-1">1st & 2nd Floor</span>
                      <p className="text-xs font-bold text-brand-navy font-montserrat leading-tight">Kiran Distributors Hub</p>
                      <p className="text-[10px] text-gray-500 font-poppins mt-0.5">Wholesale Distribution Hub</p>
                    </div>
                    <div className="p-3.5 rounded-xl border border-brand-silver/80 bg-brand-light hover:border-brand-blue/30 transition-all duration-300">
                      <span className="text-[10px] font-bold text-brand-blue font-poppins block uppercase mb-1">3rd Floor</span>
                      <p className="text-xs font-bold text-brand-navy font-montserrat leading-tight">Kiran Micro Path</p>
                      <p className="text-[10px] text-gray-500 font-poppins mt-0.5">Advanced Pathology Lab</p>
                    </div>
                  </div>
                </div>

                <p>
                  "We also started a new retail outlet in Miraj – <strong className="font-bold text-gray-900">Shushrut Medical Stores</strong>."
                </p>

                <p>
                  "Additionally, as a new service for Sangli district, we started <strong className="font-bold text-gray-900">Miraj Nuclear Centre</strong> in partnership. It is a scan centre for thyroid and many cancer-related diagnoses in Miraj, a facility that was not available in the 12 districts beyond Pune."
                </p>

                <p>
                  "In this financial year, we started a branch in Sangli that looks after hospital setups and provides all the required services."
                </p>

                {/* Expansion Grid Cards */}
                <div className="space-y-3 pt-4 border-t border-brand-silver/40">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest font-poppins">Recent Expansions</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-3.5 rounded-xl border border-brand-silver/80 bg-white hover:border-brand-blue/30 transition-all duration-300 flex flex-col justify-between shadow-sm">
                      <h5 className="text-xs font-bold text-brand-navy font-montserrat">Sushrut Medical Stores</h5>
                      <p className="text-[10px] text-gray-500 font-poppins mt-1">Established retail medicine operations in Miraj.</p>
                    </div>
                    <div className="p-3.5 rounded-xl border border-brand-silver/80 bg-white hover:border-brand-blue/30 transition-all duration-300 flex flex-col justify-between shadow-sm">
                      <h5 className="text-xs font-bold text-brand-navy font-montserrat">Miraj Nuclear Centre</h5>
                      <p className="text-[10px] text-gray-500 font-poppins mt-1">Facility available in 12 districts after Pune.</p>
                    </div>
                    <div className="p-3.5 rounded-xl border border-brand-silver/80 bg-white hover:border-brand-blue/30 transition-all duration-300 flex flex-col justify-between shadow-sm">
                      <h5 className="text-xs font-bold text-brand-navy font-montserrat">Sangli Engineering Branch</h5>
                      <p className="text-[10px] text-gray-500 font-poppins mt-1">Hospital Furniture & Equipment Dealers. Modular OT & ICU setup.</p>
                    </div>
                    <div className="p-3.5 rounded-xl border border-brand-silver/80 bg-white hover:border-brand-blue/30 transition-all duration-300 flex flex-col justify-between shadow-sm">
                      <h5 className="text-xs font-bold text-brand-navy font-montserrat">Kiran Micro Path Diagnostics</h5>
                      <p className="text-[10px] text-gray-500 font-poppins mt-1">Pathological Laboratory</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

            {/* Vision Panel */}
            <div className="bg-brand-navy text-white p-8 sm:p-12 rounded-3xl space-y-6 relative overflow-hidden shadow-lg grid-mesh hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-blue/10 transition-all duration-500 ease-out border border-transparent hover:border-brand-blue/20 group cursor-pointer">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full pointer-events-none group-hover:scale-150 group-hover:bg-white/10 transition-transform duration-700 ease-out" />
              <div className="w-12 h-12 rounded-2xl bg-brand-blue flex items-center justify-center text-white mb-4 shadow-md group-hover:rotate-12 group-hover:scale-110 transition-all duration-500 ease-out">
                <Eye size={24} />
              </div>
              <h3 className="text-2xl font-bold font-montserrat text-white">Our Vision</h3>
              <p className="text-sm text-brand-silver font-poppins leading-relaxed">
                To stand as Maharashtra's most integrated and trusted medical supply chain partner, elevating clinical infrastructure, modular surgical units, and essential critical drug channels to international standards of hygiene and reliability.
              </p>
            </div>

            {/* Mission Panel */}
            <div className="bg-brand-green text-white p-8 sm:p-12 rounded-3xl space-y-6 relative overflow-hidden shadow-lg hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-green/20 transition-all duration-500 ease-out border border-transparent hover:border-white/20 group cursor-pointer">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full pointer-events-none group-hover:scale-150 group-hover:bg-white/10 transition-transform duration-700 ease-out" />
              <div className="w-12 h-12 rounded-2xl bg-brand-navy flex items-center justify-center text-white mb-4 shadow-md group-hover:-rotate-12 group-hover:scale-110 transition-all duration-500 ease-out">
                <Compass size={24} />
              </div>
              <h3 className="text-2xl font-bold font-montserrat text-white">Our Mission</h3>
              <p className="text-sm text-emerald-50 font-poppins leading-relaxed">
                To deliver FDA-compliant medicines, state-of-the-art ICU setups, and structurally validated Class 100 Modular Operation Theatres that empower doctors, guarantee maximum patient hygiene, and optimize space utilization through cost-effective hospital architecture setups.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Journey (Timeline Layout) */}
      <section className="py-20 bg-brand-light relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">History timeline</span>
            <h2 className="text-3xl font-bold font-montserrat text-brand-navy">Our Major Milestones</h2>
          </div>

          <div ref={timelineRef} className="relative max-w-3xl mx-auto pl-12 space-y-12">
            {/* Base grey line */}
            <div className="absolute left-[15px] top-2 bottom-2 w-[2px] bg-gray-200" />

            {/* Active red progress line tracking scroll */}
            <motion.div
              className="absolute left-[15px] top-2 bottom-2 w-[2px] bg-brand-blue origin-top z-0"
              style={{ scaleY }}
            />

            {milestones.map((m, idx) => (
              <div key={idx} className="relative space-y-2">
                {/* Visual timeline node */}
                <motion.div
                  initial={{ scale: 0.8, borderColor: "#E5E7EB", borderWidth: "3px" }}
                  whileInView={{ scale: 1.15, borderColor: "#DE2929", borderWidth: "4px" }}
                  viewport={{ once: false, margin: "-15% 0px -45% 0px" }}
                  transition={{ duration: 0.3 }}
                  className="absolute -left-[44px] top-1 w-6 h-6 rounded-full bg-white flex items-center justify-center z-10"
                />

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false, margin: "-15% 0px -40% 0px" }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="pl-4"
                >
                  <span className="text-xs font-bold font-poppins text-brand-blue tracking-wide bg-brand-blue/10 px-2.5 py-1 rounded inline-block mb-1.5">
                    {m.year}
                  </span>
                  <h4 className="text-lg font-bold font-montserrat text-brand-navy">{m.title}</h4>
                  <p className="text-sm text-gray-500 font-poppins leading-relaxed mt-1">{m.desc}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">Pillars</span>
            <h2 className="text-3xl font-bold font-montserrat text-brand-navy">Core Corporate Values</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreValues.map((val, idx) => (
              <div
                key={idx}
                className="p-8 rounded-3xl border border-brand-silver bg-white shadow-sm hover:border-brand-blue hover:shadow-xl hover:shadow-brand-blue/5 hover:-translate-y-2 transition-all duration-300 space-y-4 flex flex-col justify-between group"
              >
                <div className="w-10 h-10 rounded-2xl bg-brand-blue/10 text-brand-blue flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <CheckCircle2 size={20} className="stroke-[3]" />
                </div>
                <div className="space-y-2">
                  <h4 className="font-extrabold text-lg sm:text-xl text-brand-navy font-montserrat tracking-tight group-hover:text-brand-blue transition-colors">{val.name}</h4>
                  <p className="text-sm text-gray-600 font-poppins leading-relaxed">{val.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Objectives */}
      <section className="py-20 bg-brand-light border-t border-brand-silver/50 relative">
        <div className="max-w-6xl mx-auto px-6 text-center space-y-10 relative z-10">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">Forward Path</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold font-montserrat text-brand-navy tracking-tight">Our Business Objectives</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left max-w-5xl mx-auto">
            {[
              'Equip every rural and urban Sangli district hospital with standard clinical setups.',
              'Ensure seamless availability of genuine oncology, cardiac, and general drug inventories.',
              'Drive modular Operation Theatre compliance audits aligned with NABH safety regulations.',
              'Expand local engineer presence to guarantee immediate post-installation calibration support.'
            ].map((obj, idx) => (
              <div
                key={idx}
                className="flex gap-4 bg-white p-8 rounded-3xl border border-brand-silver/80 shadow-md text-gray-700 font-poppins text-sm sm:text-base leading-relaxed hover:border-brand-blue hover:shadow-xl hover:shadow-brand-blue/5 hover:-translate-y-2 transition-all duration-300 items-start group"
              >
                <Target size={22} className="text-brand-green group-hover:scale-110 transition-transform duration-300 shrink-0 mt-1" />
                <span>{obj}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
